import { io } from 'socket.io-client';
import { getTokenAuth } from '../utils/authUtils';

/**
 * Dedicated Socket.io service for the /support namespace.
 * Separate from the general socketService so agent-specific events
 * (agent_status, ticket_assigned, new_message …) reach the correct namespace.
 */
class SupportSocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.listeners = new Map();
  }

  _getSocketOrigin() {
    const rawUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    try {
      return new URL(rawUrl, window.location.origin).origin;
    } catch (_) {
      return String(rawUrl).replace(/\/(api\/v\d+)(\/)?$/i, '').replace(/\/$/, '');
    }
  }

  connect() {
    return new Promise((resolve, reject) => {
      if (this.socket && this.connected) {
        resolve(true);
        return;
      }

      const token = getTokenAuth();
      if (!token) {
        reject(new Error('Authentication required'));
        return;
      }

      const origin = this._getSocketOrigin();
      const nsUrl = `${origin}/support`;

      this.socket = io(nsUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        path: '/socket.io',
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      this.socket.on('connect', () => {
        this.connected = true;
        // Re-attach any previously registered listeners
        this.listeners.forEach((cb, event) => this.socket.on(event, cb));
        resolve(true);
      });

      this.socket.on('connect_error', (err) => {
        this.connected = false;
        reject(err);
      });

      this.socket.on('disconnect', () => {
        this.connected = false;
      });
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  emit(event, data) {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    }
  }

  on(event, callback) {
    this.listeners.set(event, callback);
    if (this.socket) this.socket.on(event, callback);
  }

  off(event) {
    this.listeners.delete(event);
    if (this.socket) this.socket.off(event);
  }
}

export default new SupportSocketService();
